import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import FormSection from '../../../Common/Form/FormSection';
import HintCallout from '../../../Common/HintCallout';
import axiosInstance from '../../../../../services/AxiosInstance';
import Api from '../../../../../constants/Api';
import { errorAlert, errorObjectAlert, successToast, warnAlertYesOrNo } from '../../../alert/defaultAlert';
import { formatFileSize, openAuthenticatedPdf, validateContractFile } from '../../Contract/ContractUtils';

/**
 * Contratos internos do colaborador (vários PDFs, cada um com título). Documento de RH, só
 * para administrador: nunca aparece no portal do colaborador nem para o cliente. Como o
 * plano, depende do cadastro já salvo (precisa do id).
 */
const EmployeeContractsSection = ({ employeeId }) => {
    const inputRef = useRef(null);
    const [items, setItems] = useState([]);
    const [title, setTitle] = useState('');
    const [busy, setBusy] = useState(false);
    // Operador que não é administrador recebe recusa do backend; a seção apenas se explica.
    const [denied, setDenied] = useState('');
    const base = `${Api.main.employee}/${employeeId}/contracts`;

    const load = useCallback(async () => {
        try {
            const { data } = await axiosInstance.get(base);
            setItems(data);
            setDenied('');
        } catch (error) {
            setDenied(error?.response?.data?.message || 'Não foi possível carregar os contratos.');
        }
    }, [base]);

    useEffect(() => {
        if (employeeId) load();
    }, [employeeId, load]);

    if (!employeeId) {
        return (
            <FormSection title="Contratos" description="Documentos internos do colaborador (só administrador)." icon="fa-solid fa-file-contract" compact>
                <HintCallout icon="fa-info-circle">Salve o colaborador primeiro. Depois de salvo, o contrato padrão é gerado com os dados dele e você pode anexar os contratos assinados (PDF).</HintCallout>
            </FormSection>
        );
    }

    const upload = async (file) => {
        const invalid = validateContractFile(file) || (title.trim() ? '' : 'Informe o título do contrato antes de anexar.');
        if (invalid) {
            errorAlert(invalid);
            if (inputRef.current) inputRef.current.value = '';
            return;
        }
        const body = new FormData();
        body.append('file', file);
        body.append('title', title.trim());
        try {
            setBusy(true);
            await axiosInstance.post(base, body);
            setTitle('');
            successToast('Contrato anexado.');
            await load();
        } catch (error) {
            errorObjectAlert(error);
        } finally {
            setBusy(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    };

    const view = async (item) => {
        try {
            await openAuthenticatedPdf(`${base}/${item.id}/download`);
        } catch (error) {
            errorAlert(error.message || 'Não foi possível abrir o contrato.');
        }
    };

    const viewStandard = async () => {
        try {
            await openAuthenticatedPdf(`${base}/standard`);
        } catch (error) {
            errorAlert(error.message || 'Não foi possível gerar o contrato padrão.');
        }
    };

    const remove = (item) => warnAlertYesOrNo(`Excluir "${item.title}"? O arquivo é apagado e não tem como desfazer.`, async () => {
        try {
            setBusy(true);
            await axiosInstance.delete(`${base}/${item.id}`);
            await load();
        } catch (error) {
            errorObjectAlert(error);
        } finally {
            setBusy(false);
        }
    });

    return (
        <FormSection title="Contratos" description="Documentos internos do colaborador (só administrador)." icon="fa-solid fa-file-contract" compact>
            {denied ? <HintCallout icon="fa-lock">{denied}</HintCallout> : (
                <>
                    {/* Contrato padrão: gerado na hora com os dados do colaborador e da empresa. */}
                    <div className="d-flex flex-wrap align-items-center gap-2 py-2">
                        <span className="me-auto">
                            <i className="fa-solid fa-wand-magic-sparkles me-2"></i>
                            <strong>Contrato padrão</strong>
                            <span className="text-muted ms-2">gerado com os dados deste colaborador e da empresa</span>
                        </span>
                        <Button size="sm" variant="outline-secondary" onClick={viewStandard} disabled={busy}>Ver / imprimir</Button>
                    </div>
                    <hr className="my-2" />
                    {items.length === 0
                        ? <p className="text-muted mb-3">Nenhum contrato assinado anexado a este colaborador.</p>
                        : (
                            <ul className="list-unstyled mb-3">
                                {items.map((item) => (
                                    <li key={item.id} className="d-flex flex-wrap align-items-center gap-2 py-2 border-bottom">
                                        <span className="me-auto">
                                            <i className="fa-solid fa-file-pdf me-2"></i>
                                            <strong>{item.title}</strong>
                                            <span className="text-muted ms-2">{item.fileName}</span>
                                            {item.fileSize ? <span className="text-muted ms-2">{formatFileSize(item.fileSize)}</span> : null}
                                        </span>
                                        <Button size="sm" variant="outline-secondary" onClick={() => view(item)} disabled={busy}>Ver</Button>
                                        <Button size="sm" variant="outline-danger" onClick={() => remove(item)} disabled={busy}>Excluir</Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <Form.Control style={{ maxWidth: 320 }} size="sm" maxLength={120} placeholder="Título (ex.: Contrato de trabalho)"
                            value={title} onChange={(event) => setTitle(event.target.value)} disabled={busy} />
                        <Button size="sm" variant="primary" onClick={() => inputRef.current?.click()} disabled={busy}>Anexar PDF</Button>
                        <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="d-none"
                            onChange={(event) => upload(event.target.files?.[0])} />
                    </div>
                </>
            )}
        </FormSection>
    );
};

export default EmployeeContractsSection;
